import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Spinner,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiChevronLeft, FiEdit2, FiShare2 } from "react-icons/fi";
import axios from "axios";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import ShareReportModal from "../../components/ShareReportModal";
import { Loading } from "../../components/Loading";

const API_URL = import.meta.env.VITE_API_URL;

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  }

  if (Array.isArray(value)) {
    if (!value.length) return "Empty";
    if (typeof value[0] !== "object") return value.join(", ");
    return `${value.length} items`;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? `${v.length} items` : typeof v === "object" ? "Object" : v}`)
      .slice(0, 8);

    return entries.length ? entries.join(", ") : "Object";
  }

  return String(value);
};

const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const ReportDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();

  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!location.state?.report);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reportPreview, setReportPreview] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [reportAccess, setReportAccess] = useState(null);

  useEffect(() => {
    if (report) return;

    const fetchReportById = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("prima_session");
        const cleanToken = token?.replace(/^Bearer\s+/i, "") || "";

        const response = await axios.get(`${API_URL}/report/all`, {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
          },
          params: {
            page: 1,
            limit: 200,
            _t: Date.now(),
          },
        });

        const allReports = response.data?.data || [];
        const selectedReport = allReports.find((item) => item._id === id);

        if (!selectedReport) {
          toast({
            title: "Report not found",
            description: "Unable to find this report.",
            status: "warning",
            duration: 3000,
          });
          navigate("/reports/view");
          return;
        }

        setReport(selectedReport);
      } catch (error) {
        toast({
          title: "Error loading report",
          description: error.response?.data?.message || "Failed to load report details.",
          status: "error",
          duration: 4000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReportById();
  }, [API_URL, id, navigate, report, toast]);

  useEffect(() => {
    if (!report?.tables?.length || !report?.selectedColumns?.length) return;

    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        const token = localStorage.getItem("prima_session");
        const cleanToken = token?.replace(/^Bearer\s+/i, "") || "";

        const response = await axios.post(
          `${API_URL}/report/run`,
          {
            tables: report.tables,
            selectedColumns: report.selectedColumns,
          },
          {
            headers: {
              Authorization: `Bearer ${cleanToken}`,
            },
            params: {
              page: 1,
              limit: 10,
            },
          }
        );

        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setReportPreview(data);
        setPreviewTotal(response.data?.total || data.length || 0);
      } catch (error) {
        toast({
          title: "Error loading report preview",
          description: error.response?.data?.message || "Failed to load report rows.",
          status: "error",
          duration: 3500,
        });
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [API_URL, report, toast]);

  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        const selectedReportId = report?._id || id;

        if (!selectedReportId) return;

        const res = await axios.get(`${API_URL}/report/access/${selectedReportId}?_t=${Date.now()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setReportAccess(res.data?.data || {});
      } catch (error) {
        console.error("Error fetching report access:", error);
        setReportAccess(null);
      }
    };

    fetchAccess();
  }, [API_URL, id, report]);

  const breadcrumbItems = [
    { title: "Report", link: "/stats" },
    { title: "View all reports", link: "/reports/view" },
    { title: "Report details", link: `/reports/view/${id}` },
  ];

  const reportEntries = useMemo(() => {
    if (!report) return [];
    return Object.entries(report).filter(([key]) => key !== "__v");
  }, [report]);

  const previewHeaders = useMemo(() => {
    if (!report?.selectedColumns?.length) return [];
    return report.selectedColumns.map((col) => ({
      label: col.label || col.path,
      path: col.path,
    }));
  }, [report]);

  const handleShareReport = async (data) => {
    try {
      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "");
      const selectedReportId = report?._id || id;

      await axios.put(`${API_URL}/report/${selectedReportId}/share`, data, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      toast({
        title: "Report shared successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Share error:", error);
      toast({
        title: "Error sharing report",
        description: error.response?.data?.message || "Failed to share report.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!report) return null;

  return (
    <Box p={{ base: 4, md: 6 }} bg="#F7F9FB" minH="100vh">
      <BreadCrumbMaker items={breadcrumbItems} />

      <Flex justify="space-between" align="center" mt={6} mb={6}>
        <Heading size="lg" color="#303A6D">Report Details</Heading>
        <Flex gap={3}>
          <Button
            leftIcon={<FiEdit2 />}
            colorScheme="blue"
            variant="solid"
            isDisabled={reportAccess ? !reportAccess.canEdit : false}
            onClick={() => navigate(`/reports/edit/${report?._id || id}`, { state: { report } })}
          >
            Edit
          </Button>
          <Button leftIcon={<FiShare2 />} colorScheme="blue" variant="outline" onClick={onShareOpen}>
            Share
          </Button>
          <Button leftIcon={<FiChevronLeft />} variant="outline" onClick={() => navigate("/reports/view")}>
            Back to Reports
          </Button>
        </Flex>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="#D9E0F0" borderRadius="16px" p={6}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          {reportEntries.map(([key, value]) => (
            <Box key={key} p={3} border="1px solid" borderColor="#EDF2F7" borderRadius="10px">
              <Text fontSize="12px" color="#6A7DAE" textTransform="uppercase" fontWeight="700" mb={1}>
                {key.replace(/([A-Z])/g, " $1").trim()}
              </Text>

              {Array.isArray(value) && key === "selectedColumns" ? (
                <VStack align="start" spacing={2}>
                  {value.map((col, idx) => (
                    <Tag key={`${col.path || idx}`} colorScheme="blue" size="sm">
                      <TagLabel>{col.label || col.path || "Column"}</TagLabel>
                    </Tag>
                  ))}
                </VStack>
              ) : Array.isArray(value) && key === "tables" ? (
                <Flex wrap="wrap" gap={2}>
                  {value.map((table) => (
                    <Tag key={table} colorScheme="purple" size="sm">
                      <TagLabel>{table}</TagLabel>
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Text color="#303A6D" fontWeight="500" whiteSpace="normal" wordBreak="break-word">
                  {formatValue(value)}
                </Text>
              )}
            </Box>
          ))}
        </Grid>
      </Box>

      <Box mt={6} bg="white" border="1px solid" borderColor="#D9E0F0" borderRadius="16px" p={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md" color="#303A6D">Report Preview</Heading>
          <Text color="gray.600" fontSize="sm">Showing {reportPreview.length} of {previewTotal}</Text>
        </Flex>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Sr. No</Th>
                {previewHeaders.map((header) => (
                  <Th key={header.path}>{header.label}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {previewLoading ? (
                <Tr>
                  <Td colSpan={previewHeaders.length + 1} textAlign="center" py={10}>
                    <Spinner color="#3E6AE1" />
                  </Td>
                </Tr>
              ) : reportPreview.length > 0 ? (
                reportPreview.map((row, index) => (
                  <Tr key={row._id || index}>
                    <Td>{index + 1}</Td>
                    {previewHeaders.map((header) => {
                      const directValue = getNestedValue(row, header.path);
                      const normalizedPath = header.path?.includes(".")
                        ? header.path.split(".").slice(1).join(".")
                        : header.path;
                      const fallbackValue = getNestedValue(row, normalizedPath);
                      const value = directValue !== undefined ? directValue : fallbackValue;

                      return (
                        <Td key={`${header.path}-${index}`} maxW="240px" whiteSpace="normal" wordBreak="break-word">
                          {formatValue(value)}
                        </Td>
                      );
                    })}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={previewHeaders.length + 1} textAlign="center" py={8} color="gray.500">
                    No preview data available.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <ShareReportModal
        isOpen={isShareOpen}
        onClose={onShareClose}
        reportName={report.reportName}
        selectedTables={report.tables || []}
        selectedColumns={report.selectedColumns || []}
        onShare={handleShareReport}
      />
    </Box>
  );
};

export default ReportDetail;
